import { useEffect, useRef, useState } from "react";
import type { User, LogLine } from "../lib/types";
import { Sidebar } from "../components/Sidebar";
import { TopBar } from "../components/TopBar";
import { CodeEditor } from "../components/CodeEditor";
import { SandboxGraph } from "../components/SandboxGraph";
import { Chatbot } from "../components/Chatbot";
import { I } from "../components/Icons";
import {
  initialSandbox,
  run,
  editWorkingFile,
  type SandboxState,
} from "../lib/sandbox-engine";
import { CHEAT_GROUPS } from "../data/cheatsheet";

interface Props {
  user: User;
  onSignOut: () => void;
}

const WELCOME: LogLine[] = [
  { kind: "info", text: "Welcome to the Git sandbox — a safe, fake repo you can't break." },
  { kind: "out", text: 'Type any git command and watch the graph + files react. Type "help" for the full list.' },
  { kind: "out", text: "" },
];

const EXAMPLES: { label: string; cmd: string }[] = [
  { label: "Check status", cmd: "git status" },
  { label: "New branch", cmd: "git switch -c feature" },
  { label: "Edit a file", cmd: 'echo "<h1>Hello</h1>" > index.html' },
  { label: "Stage all", cmd: "git add ." },
  { label: "Commit", cmd: 'git commit -m "My change"' },
  { label: "Merge", cmd: "git merge feature" },
  { label: "History", cmd: "git log --oneline --all" },
  { label: "Undo last commit", cmd: "git reset --hard HEAD~1" },
];

export function Sandbox({ user, onSignOut }: Props) {
  const [state, setState] = useState<SandboxState>(() => initialSandbox());
  const [log, setLog] = useState<LogLine[]>(WELCOME);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState<string[]>([]);
  const [histIdx, setHistIdx] = useState<number | null>(null);
  const [activeFile, setActiveFile] = useState<string>("README.md");

  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [log]);

  // Keep the active file valid as files come and go.
  useEffect(() => {
    const names = Object.keys(state.files);
    if (names.length && !names.includes(activeFile)) setActiveFile(names[0]);
  }, [state.files, activeFile]);

  function execute(raw: string) {
    const line = raw.trim();
    if (!line) return;
    setHistory((h) => [...h, line]);
    setHistIdx(null);

    const result = run(state, line);
    // `clear` sentinel wipes the scrollback.
    if (result.lines.length === 1 && result.lines[0].text === "__CLEAR__") {
      setLog([]);
      return;
    }
    setState(result.state);
    setLog((prev) => [...prev, { kind: "cmd", text: line }, ...result.lines]);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    execute(input);
    setInput("");
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (history.length === 0) return;
      const idx = histIdx === null ? history.length - 1 : Math.max(0, histIdx - 1);
      setHistIdx(idx);
      setInput(history[idx]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (histIdx === null) return;
      const idx = histIdx + 1;
      if (idx >= history.length) {
        setHistIdx(null);
        setInput("");
      } else {
        setHistIdx(idx);
        setInput(history[idx]);
      }
    }
  }

  function reset() {
    setState(initialSandbox());
    setLog([...WELCOME, { kind: "ok", text: "Sandbox reset to a fresh repo." }]);
    setHistory([]);
    setHistIdx(null);
    setActiveFile("README.md");
  }

  function runExample(cmd: string) {
    execute(cmd);
    inputRef.current?.focus();
  }

  const hasFiles = Object.keys(state.files).length > 0;

  return (
    <div className="gf-shell">
      <Sidebar active="sandbox" user={user} onSignOut={onSignOut} />
      <div className="gf-main">
        <TopBar
          crumbs={[
            { t: "Git Fit", href: "/dashboard" },
            { t: "Sandbox", strong: true },
          ]}
          right={
            <button className="gf-btn gf-btn--secondary" onClick={reset} title="Reset to a fresh repo">
              <I.Refresh size={14} /> Reset sandbox
            </button>
          }
        />

        <div className="gf-dash">
          <div className="gf-sbx__intro">
            <div className="gf-sbx__intro-eyebrow">
              <I.Terminal size={14} /> Free-form playground
            </div>
            <h1 className="gf-sbx__intro-title">Try any Git command, safely</h1>
            <p className="gf-sbx__intro-sub">
              This is a pretend repository running entirely in your browser — nothing here touches a
              real project. Type commands in the terminal (or edit a file and stage it), and watch the
              commit graph and file tree update live. Stuck? Type <code>help</code>, or grab a command
              from the cheat sheet below.
            </p>
            <div className="gf-sbx__examples">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex.cmd}
                  className="gf-sbx__chip"
                  onClick={() => runExample(ex.cmd)}
                  title={ex.cmd}
                >
                  {ex.label}
                </button>
              ))}
            </div>
          </div>

          {/* Editor + graph */}
          <div
            className="gf-sbx__grid"
            style={{
              display: "grid",
              gridTemplateColumns: hasFiles ? "minmax(0, 1fr) minmax(0, 1.1fr)" : "1fr",
              gap: 18,
              marginTop: 22,
            }}
          >
            {hasFiles && (
              <CodeEditor
                files={state.files}
                activeFile={activeFile in state.files ? activeFile : Object.keys(state.files)[0]}
                onActiveFileChange={setActiveFile}
                onContentChange={(name, content) =>
                  setState((s) => editWorkingFile(s, name, content))
                }
              />
            )}
            <SandboxGraph state={state} />
          </div>

          {/* Terminal */}
          <div className="gf-term" style={{ marginTop: 18 }}>
            <div className="gf-term__head">
              <div className="gf-term__dots">
                <div className="gf-term__dot"></div>
                <div className="gf-term__dot"></div>
                <div className="gf-term__dot"></div>
              </div>
              <div className="gf-term__title">
                {user.name.split(" ")[0].toLowerCase()}@nymbl ~ my-app
                {state.detached ? "  (detached HEAD)" : `  (${state.head})`}
              </div>
            </div>
            <div className="gf-term__body" ref={bodyRef} style={{ maxHeight: 320 }}>
              {log.map((line, i) => (
                <div key={i} className={`gf-term__line gf-term__line--${line.kind}`}>
                  {line.kind === "cmd" ? (
                    <>
                      <span className="gf-term__prompt">$</span>
                      <span className="gf-term__cmd">{line.text}</span>
                    </>
                  ) : (
                    line.text
                  )}
                </div>
              ))}
            </div>
            <form className="gf-term__input" onSubmit={onSubmit}>
              <span className="gf-term__prompt">$</span>
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="type a git command and press Enter…  (↑ for history)"
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                autoComplete="off"
                autoFocus
              />
            </form>
          </div>

          {/* Cheat sheet */}
          <CheatSheet />
        </div>
      </div>
      <Chatbot context={{ screen: "sandbox", courseTitle: "Git sandbox" }} />
    </div>
  );
}

function CheatSheet() {
  return (
    <div className="gf-card" style={{ marginTop: 22 }}>
      <div className="gf-card__head">
        <h3>Git cheat sheet</h3>
        <div className="gf-card__head-meta">Click an example chip above to run one</div>
      </div>
      <div className="gf-card__body">
        <div className="gf-sbx__cheat-groups">
          {CHEAT_GROUPS.map((g) => (
            <div key={g.title} className="gf-sbx__cheat-group">
              <div className="gf-sbx__cheat-title">{g.title}</div>
              <div className="gf-cheat gf-cheat--stack">
                {g.items.map((c) => (
                  <div key={c.cmd} className="gf-cheat__row">
                    <div className="gf-cheat__cmd">{c.cmd}</div>
                    <div className="gf-cheat__desc">{c.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
